import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Input } from '../form/input';

interface GoogleMapsPickerProps {
  latitude: string;
  longitude: string;
  onLocationChange: (lat: string, lng: string, address?: string) => void;
  apiKey: string;
}

const GoogleMapsPicker: React.FC<GoogleMapsPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
  apiKey,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeMap = async () => {
      try {
        const loader = new Loader({
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places'],
        });

        await loader.load();
        setIsLoaded(true);

        if (mapRef.current) {
          const initialLat = parseFloat(latitude) || -34.9033;
          const initialLng = parseFloat(longitude) || -56.1833;

          const mapInstance = new google.maps.Map(mapRef.current, {
            center: { lat: initialLat, lng: initialLng },
            zoom: 15,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
          });

          const markerInstance = new google.maps.Marker({
            position: { lat: initialLat, lng: initialLng },
            map: mapInstance,
            draggable: true,
            title: 'Ubicación del club',
          });

          // Add click listener to map
          mapInstance.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();

              markerInstance.setPosition({ lat, lng });
              onLocationChange(lat.toString(), lng.toString());

              // Reverse geocoding to get address
              const geocoder = new google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                  onLocationChange(lat.toString(), lng.toString(), results[0].formatted_address);
                }
              });
            }
          });

          // Add drag listener to marker
          markerInstance.addListener('dragend', () => {
            const position = markerInstance.getPosition();
            if (position) {
              const lat = position.lat();
              const lng = position.lng();
              onLocationChange(lat.toString(), lng.toString());
            }
          });

          setMap(mapInstance);
          setMarker(markerInstance);

          // Initialize search box
          if (searchInputRef.current) {
            const searchBoxInstance = new google.maps.places.SearchBox(searchInputRef.current);

            // Bias the SearchBox results towards current map's viewport
            mapInstance.addListener('bounds_changed', () => {
              searchBoxInstance.setBounds(mapInstance.getBounds() as google.maps.LatLngBounds);
            });

            searchBoxInstance.addListener('places_changed', () => {
              const places = searchBoxInstance.getPlaces();
              if (!places || places.length === 0) return;

              const place = places[0];
              if (!place.geometry || !place.geometry.location) return;

              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();

              mapInstance.setCenter({ lat, lng });
              mapInstance.setZoom(17);
              markerInstance.setPosition({ lat, lng });

              onLocationChange(lat.toString(), lng.toString(), place.formatted_address);
            });

            setSearchBox(searchBoxInstance);
          }
        }
      } catch (err) {
        console.error('Error loading Google Maps:', err);
        setError('Error loading Google Maps. Please check your API key.');
      }
    };

    if (apiKey) {
      initializeMap();
    } else {
      setError('Google Maps API key is required');
    }
  }, [apiKey]);

  // Update marker position when coordinates change externally
  useEffect(() => {
    if (marker && map && latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const newPosition = { lat, lng };
        marker.setPosition(newPosition);
        map.setCenter(newPosition);
      }
    }
  }, [latitude, longitude, marker, map]);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ubicación en el Mapa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 text-center text-red-500">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ubicación en el Mapa</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          inputRef={searchInputRef}
          type="text"
          placeholder="Buscar una dirección o lugar..."
          className="w-full"
          data-1p-ignore
        />

        <div
          ref={mapRef}
          className="h-96 w-full rounded-lg border"
          style={{ minHeight: '384px' }}
        />

        {!isLoaded && (
          <div className="flex h-96 items-center justify-center rounded-lg bg-gray-100">
            <div className="text-gray-500">Cargando mapa...</div>
          </div>
        )}

        <p className="text-sm text-gray-600">
          Haz clic en el mapa o arrastra el marcador para seleccionar la ubicación exacta de tu
          club.
        </p>
      </CardContent>
    </Card>
  );
};

export default GoogleMapsPicker;
